import { useMemo, useState } from 'react';
import { AttachmentPrimitive, useAui, useAuiState } from '@assistant-ui/react';
import { FileText, XIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useChatCopy, useChatInterfaceLocale } from '@/lib/chat-i18n';
import { localizeWorkspaceError } from '@/lib/workspace-error-copy';
import { pastedText, pastePreview } from '@/lib/pasted-text';
import { INLINE_ATTACHMENT_MAX_BYTES, inlineAttachmentTooLarge } from '@/lib/document-attachments';
import { usePastedTextComposer } from './pasted-text-composer';

export function PastedTextAttachment() {
  const aui = useAui();
  const copy = useChatCopy();
  const locale = useChatInterfaceLocale();
  const attachment = useAuiState((state) => state.attachment);
  const text = useMemo(() => pastedText(attachment) ?? '', [attachment]);
  const actions = usePastedTextComposer();
  const editable = aui.attachment.source !== 'message' && actions !== null;
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(text);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const changeOpen = (value: boolean) => {
    if (saving) return;
    if (value) {
      setDraft(text);
      setError('');
    }
    setOpen(value);
  };
  const save = async () => {
    if (new Blob([draft]).size > INLINE_ATTACHMENT_MAX_BYTES) {
      setError(inlineAttachmentTooLarge(attachment.name));
      return;
    }
    setSaving(true);
    try {
      await actions?.save(attachment, draft);
      setOpen(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'We could not upload this file.');
    } finally {
      setSaving(false);
    }
  };
  return (
    <AttachmentPrimitive.Root className="aui-pasted-text relative w-64 max-w-full shrink-0 rounded-2xl border bg-background text-left">
      <Dialog open={open} onOpenChange={changeOpen}>
        <DialogTrigger asChild>
          <button
            type="button"
            className="flex w-full items-start gap-2 rounded-2xl p-3 pr-9 text-left hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring"
            aria-label={attachment.name}
          >
            <FileText aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-primary" />
            <span className="min-w-0">
              <span className="block truncate text-sm font-medium">
                {pastePreview(text) || attachment.name}
              </span>
              <span className="text-muted-foreground block truncate text-xs">
                {attachment.name}
              </span>
            </span>
          </button>
        </DialogTrigger>
        <DialogContent
          showCloseButton={false}
          aria-describedby={undefined}
          className="flex max-h-[85dvh] flex-col gap-3 p-4 sm:max-w-3xl"
        >
          <DialogTitle className="shrink-0 text-base">{attachment.name}</DialogTitle>
          {editable ? (
            <textarea
              aria-label={attachment.name}
              value={draft}
              onChange={(event) => {
                const value = event.target.value;
                if (new Blob([value]).size > INLINE_ATTACHMENT_MAX_BYTES) {
                  setError(inlineAttachmentTooLarge(attachment.name));
                  return;
                }
                setError('');
                setDraft(value);
              }}
              disabled={saving}
              spellCheck={false}
              className="min-h-0 h-[55dvh] w-full resize-none overflow-y-auto rounded-lg border bg-background p-3 text-base leading-relaxed"
            />
          ) : (
            <pre
              tabIndex={0}
              aria-label={attachment.name}
              className="min-h-0 max-h-[60dvh] overflow-y-auto whitespace-pre-wrap break-words rounded-lg border p-3 font-sans text-sm leading-relaxed"
            >
              {text}
            </pre>
          )}
          {error && (
            <p role="alert" className="text-destructive text-sm">
              {localizeWorkspaceError(error, locale)}
            </p>
          )}
          <DialogFooter className="shrink-0">
            <DialogClose asChild>
              <Button type="button" variant="outline" disabled={saving}>
                {copy.close}
              </Button>
            </DialogClose>
            {editable && (
              <Button type="button" disabled={saving || draft === text} onClick={() => void save()}>
                {copy.saveChanges}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {editable && (
        <>
          <button
            type="button"
            className="absolute right-1 top-1 flex size-8 items-center justify-center rounded-full text-muted-foreground hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring"
            aria-label={`${copy.removeFile}: ${attachment.name}`}
            onClick={() => void actions.remove(attachment)}
          >
            <XIcon aria-hidden="true" className="size-4" />
          </button>
          <button
            type="button"
            className="min-h-9 w-full rounded-b-2xl px-9 pb-2 text-left text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring"
            onClick={() => void actions.restore(attachment)}
          >
            {copy.showInTextField}
          </button>
        </>
      )}
    </AttachmentPrimitive.Root>
  );
}
